import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";import {
  ArrowLeft, UserPlus, UserCheck, UserMinus, Heart, Play, Star, Gamepad2,
  Globe, Youtube, MessageCircle, Twitch, BadgeCheck, Loader2, Sparkles, Users, Plus, Coins, Share2, Film, ShoppingBag, Star as StarIcon, Rocket, Trophy, Crown, Package, Megaphone, Send, Trash2, Pin, ShieldCheck,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Spinner, GameCard, EmptyState } from "@/components/Bits";
import CreatorTierBadge from "@/components/CreatorTierBadge";
import { CreatorLadder } from "@/components/CreatorLadder";
import TipJar from "@/components/TipJar";
import confetti from "canvas-confetti";
import { TrustRing, VerifiedCreatorBadge } from "@/components/TrustRing";
import { bannerClass } from "@/lib/banners";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SOCIAL_ICONS = { website: Globe, twitter: MessageCircle, youtube: Youtube, discord: MessageCircle, patreon: Heart, twitch: Twitch };

export default function CreatorProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalAmt, setGoalAmt] = useState("");
  const [goalTitle, setGoalTitle] = useState("");
  const [goalBusy, setGoalBusy] = useState(false);
  const [shoutBusy, setShoutBusy] = useState(false);
  const [bundleOpen, setBundleOpen] = useState(false);
  const [bundle, setBundle] = useState(null);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [giftOn, setGiftOn] = useState(false);
  const [giftTo, setGiftTo] = useState("");
  const [giftNote, setGiftNote] = useState("");
  const [cheerMsg, setCheerMsg] = useState("");
  const [cheerBusy, setCheerBusy] = useState(false);
  const [shoutoutBusy, setShoutoutBusy] = useState(false);

  const load = useCallback(() => { api.get(`/social/creators/${id}`).then((r) => setP(r.data)).catch(() => setP(false)); }, [id]);
  useEffect(() => { setP(null); load(); }, [load]);
  useEffect(() => {
    if (p && p.tier_reward_granted > 0) toast.success(`🎉 Level up! +${p.tier_reward_granted} AI credits for reaching ${p.tier_reward_label}`, { duration: 6000 });
  }, [p?.tier_reward_granted, p?.tier_reward_label]);
  const celebratedGoal = useRef(false);
  useEffect(() => {
    const reached = p && p.tip_goal && (p.tip_month_pct || 0) >= 100;
    if (reached && !celebratedGoal.current) {
      celebratedGoal.current = true;
      confetti({ particleCount: 140, spread: 80, origin: { y: 0.3 }, colors: ["#ec4899", "#d946ef", "#f59e0b"] });
    }
  }, [p?.tip_month_pct, p?.tip_goal]);
  // Celebrate a fan's own crown-streak milestone (4/8/12 weeks) once per milestone.
  useEffect(() => {
    const fcs = p && p.is_me && p.fan_crown_streak;
    if (fcs && fcs.is_milestone) {
      const key = `gv_fan_streak_${id}_${fcs.streak}`;
      if (!localStorage.getItem(key)) {
        confetti({ particleCount: 160, spread: 85, origin: { y: 0.3 }, colors: [fcs.tier?.color || "#f59e0b", "#f59e0b", "#ec4899"] });
        toast.success(`👑 ${fcs.streak}-week crown streak — you reached ${fcs.tier?.name} tier!`, { duration: 6000 });
        try { localStorage.setItem(key, "1"); } catch {}
      }
    }
  }, [p?.is_me, p?.fan_crown_streak, id]);

  if (p === false) return <div className="p-8"><EmptyState emoji="🔍" title="Creator not found" text="This profile doesn't exist or was removed." action={<Button onClick={() => navigate(-1)} className="rounded-full">Go back</Button>} /></div>;
  if (!p) return <Spinner />;

  const toggleFollow = async () => {
    setBusy(true);
    try {
      const { data } = p.is_following ? await api.delete(`/social/follow/${id}`) : await api.post(`/social/follow/${id}`);
      setP((s) => ({ ...s, is_following: data.following, followers: data.followers }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addFriend = async () => {
    setBusy(true);
    try { const { data } = await api.post("/social/friends/request", { to_user_id: id }); toast.success(data.status === "accepted" ? "Friend added! 🎉" : "Request sent!");
      setP((s) => ({ ...s, is_friend: data.status === "accepted", request_pending: data.status !== "accepted" }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const clip = p.best_clip;
  const clipStats = p.clip_stats || { count: 0, plays: 0, likes: 0 };

  const openGoal = () => { setGoalAmt(p.tip_goal?.amount ? String(p.tip_goal.amount) : ""); setGoalTitle(p.tip_goal?.title || ""); setGoalOpen(true); };
  const saveGoal = async () => {
    setGoalBusy(true);
    try {
      const amt = parseFloat(goalAmt);
      const { data } = await api.patch("/tips/me/goal", { amount: amt > 0 ? amt : 0, title: goalTitle });
      toast.success(data.goal ? "Support goal updated! 🎯" : "Support goal cleared");
      setGoalOpen(false);
      setP((s) => ({ ...s, tip_goal: data.goal, tip_month_total: data.month_total ?? s.tip_month_total,
        tip_month_pct: data.goal ? Math.min(100, Math.round(((data.month_total ?? 0) / data.goal.amount) * 100)) : 0 }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGoalBusy(false); }
  };

  const thankSupporters = async () => {
    setShoutBusy(true);
    try {
      const { data } = await api.post("/tips/shoutout", {});
      toast.success("Thank-you posted to your game wall! 💗");
      navigate(`/app/community/${data.game_id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setShoutBusy(false); }
  };


  const shareStore = async () => {
    const url = `${window.location.origin}/c/${id}`;
    const n = p.marketplace_count || 0;
    const text = n > 0
      ? `Check out ${p.name}'s games & ${n} item${n === 1 ? "" : "s"} in their store on GameView Studio! 🎮`
      : `Check out ${p.name}'s games on GameView Studio! 🎮`;
    try {
      if (navigator.share) { await navigator.share({ title: `${p.name} on GameView Studio`, text, url }); return; }
    } catch (e) { if (e?.name === "AbortError") return; }
    try { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Store link copied — paste it anywhere! 🔗", { description: url }); }
    catch { toast(url); }
  };

  const openBundle = async () => {
    setBundleOpen(true); setBundle(null); setGiftOn(false); setGiftTo(""); setGiftNote("");
    try { const { data } = await api.get(`/marketplace/bundle/${id}`); setBundle(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBundleOpen(false); }
  };
  const buyBundle = async () => {
    setBundleBusy(true);
    try {
      if (bundle.paid_count >= 2) {
        if (giftOn && !giftTo.trim()) { toast.error("Enter your friend's email or username to gift."); setBundleBusy(false); return; }
        const body = { origin_url: window.location.origin };
        if (giftOn) { body.gift = true; body.recipient = giftTo.trim(); body.note = giftNote.trim(); }
        const { data } = await api.post(`/marketplace/bundle/${id}/checkout`, body);
        window.location.href = data.url; return;
      }
      const { data } = await api.post(`/marketplace/bundle/${id}/acquire-free`);
      toast.success(data.message || "Added to your library! 🎉");
      setBundleOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBundleBusy(false); }
  };

  const postCheer = async () => {
    const msg = cheerMsg.trim();
    if (!msg) return;
    setCheerBusy(true);
    try {
      const { data } = await api.post(`/social/creators/${id}/cheer`, { message: msg });
      setCheerMsg("");
      setP((s) => ({ ...s, cheers: [data.cheer, ...(s.cheers || [])], cheer_count: (s.cheer_count || 0) + 1, my_cheer: true }));
      toast.success("Cheer posted! 📣");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setCheerBusy(false); }
  };
  const removeCheer = async (cheerId) => {
    try {
      await api.delete(`/social/creators/${id}/cheer/${cheerId}`);
      setP((s) => ({ ...s, cheers: (s.cheers || []).filter((c) => c.id !== cheerId), cheer_count: Math.max(0, (s.cheer_count || 1) - 1) }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pinCheer = async (cheerId) => {
    try {
      const { data } = await api.post(`/social/creators/${id}/cheer/${cheerId}/pin`);
      setP((s) => {
        const cheers = (s.cheers || []).map((c) => c.id === cheerId ? { ...c, pinned: data.pinned, pinned_at: data.pinned ? new Date().toISOString() : null } : c);
        cheers.sort((a, b) => ((b.pinned ? 1 : 0) - (a.pinned ? 1 : 0)) || String(b.pinned_at || b.created_at).localeCompare(String(a.pinned_at || a.created_at)));
        return { ...s, cheers };
      });
      toast.success(data.pinned ? "Pinned to the top! 📌" : "Unpinned");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const heartCheer = async (cheerId) => {
    try {
      const { data } = await api.post(`/social/creators/${id}/cheer/${cheerId}/heart`);
      setP((s) => ({ ...s, cheers: (s.cheers || []).map((c) => c.id === cheerId ? { ...c, creator_hearted: data.hearted } : c) }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendTopFanShoutout = async () => {
    setShoutoutBusy(true);
    try {
      await api.post(`/social/creators/${id}/top-fan-shoutout`);
      toast.success("Shoutout sent to your Top Fan! 🌟");
      setP((s) => ({ ...s, top_fan_shoutout_sent: true }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setShoutoutBusy(false); }
  };

  return (
    <div className="p-5 lg:p-8 max-w-5xl mx-auto" data-testid="creator-profile">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-4" data-testid="creator-back"><ArrowLeft className="w-4 h-4" /> Back</button>

      {/* Header */}
      <div className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden mb-6">
        <div className={`h-28 ${bannerClass(p.banner)}`} data-testid="creator-banner" />
        <div className="px-6 pb-6 -mt-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="flex items-end gap-4">
              <div className="w-24 h-24 rounded-2xl grid place-items-center text-5xl bg-gradient-to-br from-[#7c3aed] to-[#3b9eff] border-4 border-[hsl(var(--card))] shrink-0">{p.avatar_emoji}</div>
              <div className="pb-1">
                <div className="font-display text-2xl font-extrabold flex items-center gap-2 flex-wrap" data-testid="creator-name">{p.name}{p.verified && <BadgeCheck className="w-6 h-6 text-[hsl(var(--primary))]" />}
                  {p.is_verified_creator && <VerifiedCreatorBadge />}
                  {p.certified_creator && (
                    <span data-testid="creator-certified-badge"
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-violet-500/15 border border-violet-500/40 text-violet-500 dark:text-violet-300"
                      title={`Certified Creator — ${p.certified_games} game${p.certified_games === 1 ? "" : "s"} passed the AI robot playtest with 80+`}>
                      <ShieldCheck className="w-3.5 h-3.5" /> Certified Creator{p.certified_games > 1 ? ` ×${p.certified_games}` : ""}
                    </span>
                  )}
                  <CreatorTierBadge tier={p.creator_tier} size="lg" />
                  {p.is_founding_creator && (
                    <span data-testid="creator-founding-badge"
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gradient-to-r from-amber-400/20 to-fuchsia-500/20 border border-amber-400/40 text-amber-500"
                      title={`Founding Creator #${p.founding_number} — one of the first to join GameView Studio`}>
                      <Rocket className="w-3.5 h-3.5" /> Founding Creator{p.founding_number ? ` #${p.founding_number}` : ""}
                    </span>
                  )}
                  {p.is_creator_of_week && (
                    <span data-testid="creator-of-week-badge"
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gradient-to-r from-amber-400/25 to-orange-500/25 border border-amber-400/50 text-amber-500"
                      title="Creator of the Week — biggest momentum on GameView Studio this week">
                      <Trophy className="w-3.5 h-3.5" /> Creator of the Week
                    </span>
                  )}
                  {p.is_month_champion && (
                    <span data-testid="creator-month-champion-badge"
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gradient-to-r from-yellow-400/25 to-amber-500/25 border border-yellow-400/50 text-yellow-500"
                      title="Champion of the Month — most weekly wins this month">
                      <Crown className="w-3.5 h-3.5 fill-yellow-500" /> Champion of the Month
                    </span>
                  )}
                  {p.top_fan_of && (
                    <span data-testid="creator-top-fan-of-badge"
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-gradient-to-r from-amber-400/25 to-orange-500/25 border border-amber-400/50 text-amber-500"
                      title={`Top Fan of ${p.top_fan_of.creator_name} this week`}>
                      <Star className="w-3.5 h-3.5 fill-amber-400" /> Top Fan of {p.top_fan_of.creator_name}
                    </span>
                  )}
                  {p.fan_crown_streak && (
                    <span data-testid="creator-fan-crown-streak"
                      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border"
                      style={{ borderColor: `${p.fan_crown_streak.tier?.color || "#f59e0b"}99`, background: `${p.fan_crown_streak.tier?.color || "#f59e0b"}22`, color: p.fan_crown_streak.tier?.color || "#f59e0b" }}
                      title={`${p.fan_crown_streak.tier?.name || "Reigning"} Fan Champion — #1 supporter ${p.fan_crown_streak.streak} weeks running`}>
                      {p.fan_crown_streak.tier?.emoji || "👑"} {p.fan_crown_streak.tier?.name || "Reigning"} · {p.fan_crown_streak.streak}x
                    </span>
                  )}
                </div>
                <div className="text-sm text-[hsl(var(--muted-foreground))] capitalize">{p.role} · Level {p.level} {p.online && <span className="text-emerald-500 font-semibold ml-1">● Online</span>}</div>
              </div>
            </div>
            {!p.is_me && (
              <div className="flex gap-2 pb-1">
                <Button onClick={toggleFollow} disabled={busy} data-testid="creator-follow-btn"
                  className={`rounded-full ${p.is_following ? "bg-[hsl(var(--secondary))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--secondary))]/80" : "bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]"}`}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : p.is_following ? <UserMinus className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                  {p.is_following ? "Following" : "Follow"}
                </Button>
                {p.is_friend ? <Button variant="outline" className="rounded-full" disabled data-testid="creator-friend-state"><UserCheck className="w-4 h-4" /> Friends</Button> :
                  p.request_pending ? <Button variant="outline" className="rounded-full" disabled>Requested</Button> :
                    <Button variant="outline" onClick={addFriend} disabled={busy} className="rounded-full" data-testid="creator-add-friend"><Plus className="w-4 h-4" /> Add friend</Button>}
                <Button variant="outline" onClick={() => navigate(`/app/messages?to=${id}`)} className="rounded-full" data-testid="creator-message-btn"><MessageCircle className="w-4 h-4" /> Message</Button>
                <TipJar creatorId={id} creatorName={p.name} supporters={p.tips_count} total={p.tips_total} asButton />
                <Button variant="outline" onClick={shareStore} className="rounded-full" data-testid="creator-share-btn"><Share2 className="w-4 h-4" /> Share</Button>
              </div>
            )}
            {p.is_me && (
              <div className="flex gap-2 pb-1">
                <Button onClick={shareStore} className="rounded-full bg-gradient-to-r from-[#f59e0b] to-[#ec4899]" data-testid="creator-share-store-btn">
                  <Share2 className="w-4 h-4" /> Share my store
                </Button>
                <Button variant="outline" onClick={() => navigate("/app/marketplace")} className="rounded-full" data-testid="creator-manage-store-btn">
                  <ShoppingBag className="w-4 h-4" /> {p.marketplace_count > 0 ? "Manage listings" : "Sell an asset"}
                </Button>
              </div>
            )}
          </div>
          {p.bio && <p className="text-sm mt-4 max-w-2xl" data-testid="creator-bio">{p.bio}</p>}

          {p.games_count > 0 && (
            <div className="mt-4 inline-flex items-center gap-3 rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] pl-2 pr-5 py-2" data-testid="creator-trust-panel">
              <TrustRing score={p.trust_score} size={56} />
              <div>
                <div className="font-display text-sm font-extrabold flex items-center gap-1.5">Creator Trust Score</div>
                <div className="text-[12px] text-[hsl(var(--muted-foreground))]">🛡️ {p.verified_games} of {p.games_count} games verified original</div>
              </div>
            </div>
          )}

          {/* Monthly support goal — progress toward the creator's tip goal for this month */}
          {(p.tip_goal || p.is_me) && (
            <div className="mt-4 rounded-2xl border border-pink-500/25 bg-pink-500/[0.05] p-4 max-w-md" data-testid="creator-support-goal">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-sm font-display font-extrabold flex items-center gap-1.5"><Heart className="w-4 h-4 text-pink-500 fill-pink-500" /> {p.tip_goal?.title || "Monthly support goal"}
                  {p.tip_goal && (p.tip_month_pct || 0) >= 100 && (
                    <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white" data-testid="creator-goal-reached">🎉 Goal smashed!</span>
                  )}
                </div>
                {p.is_me && <button onClick={openGoal} data-testid="creator-goal-edit" className="text-xs font-semibold text-pink-500 hover:underline">{p.tip_goal ? "Edit" : "Set a goal"}</button>}
              </div>
              {p.tip_goal ? (
                <>
                  <div className="relative h-3 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                    <div className="h-full rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-500 transition-all" style={{ width: `${Math.min(100, p.tip_month_pct || 0)}%` }} data-testid="creator-goal-bar" />
                    {[25, 50, 75].map((m) => (
                      <span key={m} className="absolute top-0 bottom-0 w-px bg-[hsl(var(--background))]/70" style={{ left: `${m}%` }} />
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-xs mt-1.5">
                    <span className="font-bold text-pink-500">${(p.tip_month_total || 0).toFixed(0)} <span className="text-[hsl(var(--muted-foreground))] font-normal">of ${p.tip_goal.amount.toFixed(0)} this month</span></span>
                    <span className="text-[hsl(var(--muted-foreground))]">{Math.min(100, p.tip_month_pct || 0)}%</span>
                  </div>
                  {(() => {
                    const pct = p.tip_month_pct || 0;
                    const nudge = pct >= 100 ? "Goal smashed — thank you, supporters! 🎉"
                      : pct >= 75 ? "So close! 75% there — a couple more tips to hit the goal 🔥"
                      : pct >= 50 ? "Halfway there! 💗 Help push this goal over the line"
                      : pct >= 25 ? "25% funded — the momentum is building!"
                      : "Just getting started — be the first to chip in!";
                    return <p className="text-[11px] text-pink-500/80 mt-1" data-testid="creator-goal-nudge">{nudge}</p>;
                  })()}
                  {p.is_me && (p.tip_month_pct || 0) >= 100 && (
                    <button onClick={thankSupporters} disabled={shoutBusy} data-testid="creator-thank-supporters"
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-500 text-white disabled:opacity-50">
                      {shoutBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Heart className="w-3.5 h-3.5 fill-white" />} Thank my supporters
                    </button>
                  )}
                </>
              ) : (
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Set a monthly support goal so fans can help fund your next game.</p>
              )}
            </div>
          )}

          {/* Stats row */}
          <div className="flex flex-wrap gap-6 mt-4 text-sm" data-testid="creator-stats">
            <Stat label="Games" value={p.games_count} />
            <Stat label="Followers" value={p.followers} />
            <Stat label="Following" value={p.following} />
            <Stat label="Total plays" value={p.total_plays} />
            <Stat label="Hours played" value={p.hours_played ?? 0} />
            {clipStats.count > 0 && <Stat label="🎬 Clip plays" value={clipStats.plays} testid="creator-clip-plays" />}
            {p.tips_count > 0 && <Stat label="❤️ Supporters" value={p.tips_count} testid="creator-supporters" />}
            {p.rating_count > 0 && <Stat label="Avg rating" value={`★ ${p.avg_rating}`} />}
          </div>

          {/* Private revenue (only the creator/admin sees this) */}
          {p.revenue !== null && p.revenue !== undefined && (
            <div className="mt-4 inline-flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5" data-testid="creator-revenue">
              <Coins className="w-5 h-5 text-emerald-500 shrink-0" />
              <div>
                <div className="font-display text-xl font-extrabold text-emerald-500 tabular-nums">${p.revenue.toFixed(2)}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Lifetime revenue · only you can see this</div>
              </div>
            </div>
          )}

          {/* Social links */}
          {Object.keys(p.social_links || {}).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="creator-social-links">
              {Object.entries(p.social_links).map(([k, v]) => {
                const Icon = SOCIAL_ICONS[k] || Globe;
                const href = v.startsWith("http") ? v : `https://${v}`;
                return <a key={k} href={href} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]/20 transition-colors capitalize"><Icon className="w-3.5 h-3.5" /> {k}</a>;
              })}
            </div>
          )}

          {/* Badges */}
          {p.badges?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4" data-testid="creator-badges">
              {p.badges.map((b, i) => <Badge key={i} variant="secondary" className="gap-1"><span>{b.icon}</span> {b.title}</Badge>)}
            </div>
          )}
        </div>
      </div>

      {/* Creator level ladder — New Creator → GameView Partner */}
      {p.creator_tier_ladder?.length > 0 && (
        <Section title="Creator level" icon={Sparkles}>
          <CreatorLadder ladder={p.creator_tier_ladder} current={p.creator_tier} />
        </Section>
      )}

      {/* Featured cinematic trailer */}
      {p.featured_trailer && (
        <Section title="Featured trailer" icon={Film}>
          <div className="rounded-2xl overflow-hidden border border-amber-500/30 bg-black" data-testid="creator-featured-trailer">
            <video src={`${BACKEND}${p.featured_trailer.url}`} controls autoPlay muted loop playsInline preload="metadata"
              className="w-full bg-black" style={{ aspectRatio: "16/9" }} data-testid="creator-featured-trailer-video" />
          </div>
          <button onClick={() => navigate(`/app/arcade/${p.featured_trailer.game_id}`)} data-testid="creator-featured-trailer-play"
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))] hover:underline">
            <Play className="w-4 h-4" /> {p.featured_trailer.cover_emoji} Play {p.featured_trailer.title}
          </button>
        </Section>
      )}

      {/* Marketplace listings — buy this creator's assets in one tap */}
      {p.marketplace_listings?.length > 0 && (
        <Section title={`${p.name}'s Marketplace`} icon={ShoppingBag}>
          {!p.is_me && p.marketplace_listings.length >= 2 && (
            <button onClick={openBundle} data-testid="creator-bundle-btn"
              className="mb-3 w-full sm:w-auto inline-flex items-center gap-2 rounded-2xl border border-fuchsia-500/40 bg-gradient-to-r from-fuchsia-500/15 to-[#3b9eff]/15 px-4 py-2.5 text-sm font-bold text-fuchsia-300 hover:from-fuchsia-500/25 hover:to-[#3b9eff]/25 transition-colors">
              <Package className="w-4 h-4" /> Buy {p.name}'s whole pack — save up to 25%
            </button>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="creator-marketplace">
            {p.marketplace_listings.map((l) => (
              <button key={l.id} onClick={() => navigate(`/app/marketplace?listing=${l.id}`)} data-testid={`creator-listing-${l.id}`}
                className="group text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden hover:border-[hsl(var(--primary))]/50 hover:-translate-y-0.5 transition-all">
                <div className="aspect-square grid place-items-center text-5xl bg-gradient-to-br from-[#7c3aed]/15 to-[#3b9eff]/15 relative overflow-hidden">
                  {l.preview_image
                    ? <img src={l.preview_image.startsWith("http") ? l.preview_image : `${BACKEND}${l.preview_image}`} alt={l.title} className="w-full h-full object-cover" loading="lazy" />
                    : <span>{l.preview_emoji}</span>}
                  {l.owned && <span className="absolute top-1.5 right-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500 text-white">Owned</span>}
                </div>
                <div className="p-2.5">
                  <div className="text-sm font-semibold truncate leading-snug">{l.title}</div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className={`text-sm font-extrabold ${l.price > 0 ? "text-[hsl(var(--primary))]" : "text-emerald-500"}`}>{l.price > 0 ? `$${l.price.toFixed(2)}` : "Free"}</span>
                    {l.rating_count > 0 && <span className="flex items-center gap-0.5 text-[11px] text-[hsl(var(--muted-foreground))]"><StarIcon className="w-3 h-3 fill-amber-400 text-amber-400" /> {l.rating_avg?.toFixed(1)}</span>}
                  </div>
                  <span className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--primary))] group-hover:underline"><ShoppingBag className="w-3 h-3" /> {l.owned ? "In your library" : "View & buy"}</span>
                </div>
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Top gameplay clip — clip performance as creator reputation */}
      {clip && (
        <Section title="Top gameplay clip" icon={Film}>
          <div className="rounded-2xl border border-fuchsia-500/25 bg-fuchsia-500/[0.05] p-4 flex flex-col sm:flex-row gap-4" data-testid="creator-best-clip">
            <video src={`${BACKEND}${clip.url}`} controls muted playsInline loop preload="metadata"
              className="w-full sm:w-72 rounded-xl border border-white/10 bg-black shrink-0" style={{ aspectRatio: "16/9" }} data-testid="creator-best-clip-video" />
            <div className="flex-1 min-w-0">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-fuchsia-500/20 text-fuchsia-300 mb-2"><Film className="w-3.5 h-3.5" /> Most-loved clip</span>
              <p className="font-semibold leading-snug">{clip.caption || "A wild gameplay moment"}</p>
              <button onClick={() => navigate(`/app/arcade/${clip.game_id}`)} data-testid="creator-best-clip-game"
                className="text-sm text-[hsl(var(--primary))] font-semibold hover:underline mt-1 inline-flex items-center gap-1">
                <span>{clip.cover_emoji || "🎮"}</span> {clip.game_title || "Play game"}
              </button>
              <div className="flex items-center gap-4 text-xs text-[hsl(var(--muted-foreground))] mt-2">
                <span className="flex items-center gap-1"><Play className="w-3 h-3" /> {clip.plays || 0} plays</span>
                <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> {clip.likes || 0} likes</span>
                {clipStats.count > 1 && <span>· {clipStats.count} clips total</span>}
              </div>
              <Button size="sm" variant="outline" onClick={() => navigate("/app/clips")} className="rounded-full mt-3" data-testid="creator-best-clip-more">
                <Film className="w-3.5 h-3.5" /> Explore Community Clips
              </Button>
            </div>
          </div>
        </Section>
      )}

      {/* Featured */}
      {p.featured?.length > 0 && (
        <Section title="Featured games" icon={Sparkles}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {p.featured.map((g) => <GameCard key={g.id} game={g} badge="Featured" onClick={() => navigate(`/app/arcade/${g.id}`)} />)}
          </div>
        </Section>
      )}

      {/* Wall of Fans — top supporters get public credit */}
      {p.top_supporters?.length > 0 && (
        <Section title="Wall of Fans" icon={Heart}>
          {p.top_fan_week && (
            <div className="mb-3 flex items-center gap-2 flex-wrap rounded-2xl border border-amber-400/40 bg-amber-400/10 px-3.5 py-2" data-testid="top-fan-week">
              <span className="text-lg">{p.top_fan_week.avatar_emoji}</span>
              <div className="leading-tight">
                <div className="text-[11px] font-bold text-amber-400 uppercase tracking-wide flex items-center gap-1"><Star className="w-3 h-3 fill-amber-400" /> Top Fan this week</div>
                <div className="text-sm font-bold">{p.top_fan_week.name} <span className="text-[hsl(var(--muted-foreground))] font-normal">· ${p.top_fan_week.total.toFixed(0)} in 7 days</span></div>
              </div>
              {p.is_me && (
                <button onClick={sendTopFanShoutout} disabled={shoutoutBusy || p.top_fan_shoutout_sent} data-testid="top-fan-shoutout-btn"
                  className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-black disabled:opacity-50">
                  {shoutoutBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {p.top_fan_shoutout_sent ? "Shoutout sent 🌟" : "Send a shoutout"}
                </button>
              )}
            </div>
          )}
          <div className="flex flex-wrap gap-3" data-testid="wall-of-fans">
            {p.top_supporters.map((s, i) => {
              const tier = s.total >= 40
                ? { label: "Gold", ring: "ring-2 ring-yellow-400", chip: "border-yellow-400/60 bg-yellow-400/10", text: "text-yellow-500" }
                : s.total >= 25
                ? { label: "Silver", ring: "ring-2 ring-slate-300", chip: "border-slate-300/60 bg-slate-300/10", text: "text-slate-400" }
                : { label: "Bronze", ring: "ring-2 ring-amber-700", chip: "border-amber-700/60 bg-amber-700/10", text: "text-amber-600" };
              return (
                <button key={s.id} onClick={() => navigate(`/app/creators/${s.id}`)} data-testid={`supporter-${s.id}`}
                  className={`flex items-center gap-2 rounded-full border pl-1.5 pr-3 py-1 card-hover ${tier.chip}`}>
                  <span className={`relative w-9 h-9 rounded-full grid place-items-center text-lg bg-black/10 ${tier.ring}`} title={`${tier.label} supporter`}>
                    {s.avatar_emoji}
                    {i === 0 && <span className="absolute -top-1.5 -right-1 text-xs">👑</span>}
                    {s.top_fan_week && <span className="absolute -bottom-1.5 -right-1.5 text-xs" title="Top Fan this week">⭐</span>}
                  </span>
                  <div className="leading-tight text-left">
                    <div className="text-sm font-bold flex items-center gap-1">{s.name}
                      {s.top_fan_week && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-400" data-testid={`top-fan-badge-${s.id}`}>TOP FAN</span>}
                    </div>
                    <div className={`text-[10px] font-bold ${tier.text}`}>{tier.label} · ${s.total.toFixed(0)}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </Section>
      )}

      {/* Cheers — public fan shoutouts */}
      <Section title={`Cheers${p.cheer_count ? ` (${p.cheer_count})` : ""}`} icon={Megaphone}>
        {!p.is_me && (
          <div className="flex gap-2 mb-4 max-w-lg" data-testid="cheer-form">
            <Input value={cheerMsg} onChange={(e) => setCheerMsg(e.target.value)} maxLength={200}
              onKeyDown={(e) => { if (e.key === "Enter" && !cheerBusy) postCheer(); }}
              placeholder={p.my_cheer ? "You already cheered today — come back tomorrow! 🎉" : `Leave ${p.name} a cheer…`}
              disabled={p.my_cheer || cheerBusy} data-testid="cheer-input" className="rounded-full" />
            <Button onClick={postCheer} disabled={p.my_cheer || cheerBusy || !cheerMsg.trim()} data-testid="cheer-submit"
              className="rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-black shrink-0">
              {cheerBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Cheer
            </Button>
          </div>
        )}
        {p.cheers?.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-3" data-testid="cheers-list">
            {p.cheers.map((c) => (
              <div key={c.id} data-testid={`cheer-${c.id}`}
                className={`group relative flex items-start gap-2.5 rounded-2xl border p-3 ${c.pinned ? "border-amber-400/50 bg-amber-400/[0.08]" : "border-amber-400/25 bg-amber-400/[0.05]"}`}>
                <button onClick={() => navigate(`/app/creators/${c.fan_id}`)} className="text-2xl shrink-0 leading-none mt-0.5" title={c.fan_name}>{c.fan_avatar}</button>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-bold flex items-center gap-1.5">{c.fan_name}
                    {c.pinned && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400/25 text-amber-400" data-testid={`cheer-pinned-${c.id}`}>📌 Pinned</span>}
                    {c.creator_hearted && <span className="text-[10px]" title={`${p.name} loved this`} data-testid={`cheer-hearted-${c.id}`}>❤️</span>}
                  </div>
                  <p className="text-sm leading-snug break-words">{c.message}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {p.is_me && (
                    <>
                      <button onClick={() => heartCheer(c.id)} data-testid={`cheer-heart-${c.id}`} title="Heart this cheer"
                        className={`transition-opacity ${c.creator_hearted ? "text-pink-500" : "opacity-0 group-hover:opacity-100 text-[hsl(var(--muted-foreground))] hover:text-pink-500"}`}>
                        <Heart className={`w-3.5 h-3.5 ${c.creator_hearted ? "fill-pink-500" : ""}`} />
                      </button>
                      <button onClick={() => pinCheer(c.id)} data-testid={`cheer-pin-${c.id}`} title={c.pinned ? "Unpin" : "Pin to top"}
                        className={`transition-opacity ${c.pinned ? "text-amber-400" : "opacity-0 group-hover:opacity-100 text-[hsl(var(--muted-foreground))] hover:text-amber-400"}`}>
                        <Pin className={`w-3.5 h-3.5 ${c.pinned ? "fill-amber-400" : ""}`} />
                      </button>
                    </>
                  )}
                  {(p.is_me || c.fan_id === user?.id) && (
                    <button onClick={() => removeCheer(c.id)} data-testid={`cheer-remove-${c.id}`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[hsl(var(--muted-foreground))] hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="cheers-empty">{p.is_me ? "No cheers yet — share your profile and your fans will leave some love! 💛" : `Be the first to cheer ${p.name}! 📣`}</p>
        )}
      </Section>

      {/* All games */}
      <Section title={`Games by ${p.name}`} icon={Gamepad2}>
        {p.games.length === 0 ? <EmptyState emoji="🎮" title="No published games yet" text={p.is_me ? "Publish a game to show it on your profile!" : "This creator hasn't published any games yet."} /> :
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="creator-games">
            {p.games.map((g) => <GameCard key={g.id} game={g} onClick={() => navigate(`/app/arcade/${g.id}`)} />)}
          </div>}
      </Section>

      {/* Monthly support goal editor (own profile) */}
      <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
        <DialogContent data-testid="creator-goal-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Heart className="w-5 h-5 text-pink-500 fill-pink-500" /> Monthly support goal</DialogTitle>
            <DialogDescription>Set a monthly tipping goal so fans can see your progress and help fund your next game. Leave the amount at 0 to remove it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Goal amount (USD / month)</label>
              <Input type="number" min="0" value={goalAmt} onChange={(e) => setGoalAmt(e.target.value)} placeholder="e.g. 100" data-testid="creator-goal-amount" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Title (optional)</label>
              <Input maxLength={80} value={goalTitle} onChange={(e) => setGoalTitle(e.target.value)} placeholder="e.g. Fund my next game" data-testid="creator-goal-title" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setGoalOpen(false)}>Cancel</Button>
            <Button className="rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-500" onClick={saveGoal} disabled={goalBusy} data-testid="creator-goal-save">
              {goalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save goal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Buy the whole pack — bundle discount */}
      <Dialog open={bundleOpen} onOpenChange={setBundleOpen}>
        <DialogContent data-testid="creator-bundle-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5 text-fuchsia-400" /> {p.name}'s whole pack</DialogTitle>
            <DialogDescription>Grab everything {p.name} sells in one tap.</DialogDescription>
          </DialogHeader>
          {!bundle ? <div className="py-8 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-400" /></div> : (
            bundle.item_count === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))] py-4" data-testid="bundle-empty">You already own everything in {p.name}'s store! 🎉</p>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="bundle-items">
                  {bundle.items.map((it) => (
                    <div key={it.id} className="aspect-square rounded-xl grid place-items-center text-2xl bg-gradient-to-br from-fuchsia-500/15 to-[#3b9eff]/15 border border-[hsl(var(--border))] overflow-hidden" title={it.title}>
                      {it.preview_image ? <img src={it.preview_image.startsWith("http") ? it.preview_image : `${BACKEND}${it.preview_image}`} alt={it.title} className="w-full h-full object-cover" /> : <span>{it.preview_emoji}</span>}
                    </div>
                  ))}
                </div>
                <div className="rounded-2xl border border-fuchsia-500/30 bg-fuchsia-500/[0.06] p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[hsl(var(--muted-foreground))]">{bundle.item_count} item{bundle.item_count === 1 ? "" : "s"}{bundle.free_count > 0 ? ` · ${bundle.free_count} free` : ""}</span>
                    {bundle.discount_pct > 0 && <span className="font-bold text-emerald-400" data-testid="bundle-discount">Save {bundle.discount_pct}% (${bundle.savings.toFixed(2)})</span>}
                  </div>
                  {bundle.paid_count >= 2 ? (
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-display text-3xl font-extrabold text-fuchsia-400" data-testid="bundle-price">${bundle.bundle_price.toFixed(2)}</span>
                      <span className="text-sm line-through text-[hsl(var(--muted-foreground))]">${bundle.full_price.toFixed(2)}</span>
                    </div>
                  ) : (
                    <div className="font-display text-2xl font-extrabold text-emerald-400 mt-1" data-testid="bundle-price">{bundle.paid_count === 1 ? `$${bundle.full_price.toFixed(2)} + free items` : "All free!"}</div>
                  )}
                  {bundle.paid_count === 1 && <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Bundle discounts unlock with 2+ paid items — this pack has one paid item.</p>}
                </div>
                {bundle.paid_count >= 2 && (
                  <div className="rounded-2xl border border-[hsl(var(--border))] p-3" data-testid="bundle-gift">
                    <label className="flex items-center justify-between gap-3 cursor-pointer">
                      <span className="text-sm font-semibold flex items-center gap-2">🎁 Send as a gift</span>
                      <Switch checked={giftOn} onCheckedChange={setGiftOn} data-testid="bundle-gift-toggle" />
                    </label>
                    {giftOn && (
                      <div className="mt-3 space-y-2">
                        <Input value={giftTo} onChange={(e) => setGiftTo(e.target.value)} data-testid="bundle-gift-recipient"
                          placeholder="Friend's email or username" className="rounded-xl" />
                        <Textarea value={giftNote} onChange={(e) => setGiftNote(e.target.value)} maxLength={240} data-testid="bundle-gift-note"
                          placeholder="Add a personal note (optional)…" className="rounded-xl min-h-[60px]" />
                        <p className="text-[11px] text-[hsl(var(--muted-foreground))]">The pack lands in your friend's library and we'll let them know it's from you. 💜</p>
                      </div>
                    )}
                  </div>
                )}
                <Button onClick={buyBundle} disabled={bundleBusy} data-testid="bundle-buy-btn"
                  className="w-full rounded-full bg-gradient-to-r from-fuchsia-500 to-[#3b9eff] font-bold">
                  {bundleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (giftOn ? <span className="text-base">🎁</span> : <ShoppingBag className="w-4 h-4" />)}
                  {giftOn ? `Gift the pack — $${bundle.bundle_price.toFixed(2)}` : bundle.paid_count >= 2 ? `Buy the pack — $${bundle.bundle_price.toFixed(2)}` : bundle.paid_count === 1 ? "Get the pack" : "Grab all — Free"}
                </Button>
              </div>
            )
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return <div data-testid={testid}><div className="font-display text-xl font-extrabold">{value}</div><div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div></div>;
}
function Section({ title, icon: Icon, children }) {
  return (
    <div className="mb-8">
      <h2 className="font-display text-lg font-bold mb-3 flex items-center gap-2">{Icon && <Icon className="w-5 h-5 text-[hsl(var(--primary))]" />} {title}</h2>
      {children}
    </div>
  );
}
